import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, PAYROLL_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Icon } from "@/components/Icon";
import { Why } from "@/components/Why";
import { usePaidLeave } from "@/hooks/usePaidLeave";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import type { PaidLeaveProgramOverrideRequest, PaidLeaveProgramResponse } from "@/api/types";
import { formatMoney, formatMoneyWhole } from "@/lib/format";
import { PAY_PERIOD_LABEL } from "@/pages/payroll-fixtures";
import "./PaidLeave.css";

/**
 * MN Paid Leave — the compliance work, made visible.
 *
 * Item 4 on Christy's list (docs/demo/tradeshow-2026-09.md, "What Christy wants to
 * show") is evidence the current product is being cared for. Until this screen existed
 * that evidence was a 37-test calculation engine nobody could see:
 * EasyFarm.PaidLeave was referenced by EasyFarm.Api.csproj and called by nothing.
 *
 * EVERY figure on this screen comes back from POST /api/payroll/paid-leave, which
 * delegates to that engine — the premiums from PaidLeaveCalculator, the quarterly
 * rollup from WageDetailAggregator, the downloadable file from
 * DelimitedWageDetailWriter, and the rates and wage base from StateProgramCatalog. This
 * component holds no copy of the MN percentages: the "0.44%" it prints is
 * `program.rateEmployee` scaled for display, the same way formatMoney turns a server
 * decimal into "$1,540.00". If the engine's rate changed tomorrow, this screen would
 * change with it and nothing here would need editing.
 *
 * The inputs — who is on the roster, what they grossed, how many hours — come from the
 * same place the Payroll screen gets them: GET /api/demo/employees (usePayrollRoster),
 * real rows in the `employee` table rather than the TypeScript array both screens used to
 * import. What the demo does NOT know is stated on the screen rather than filled in with
 * something plausible: see the disclosure block at the bottom, and the doc on
 * `PayrollEmployee` in `api/types.ts` for why the H-2A flag is not wired to the engine's
 * paid-leave exemption input — that determination has not been made, and the roster
 * knowing who is H-2A does not make it.
 *
 * THE PROGRAM PARAMETERS ARE EDITABLE, and that does not weaken any of the above. The
 * three boxes below "Program in force" do not compute anything: they send the employer's
 * figures TO the engine, which recomputes the entire run — premiums, totals, quarterly
 * rollup, and the bytes of the downloadable file — and sends it back, and this screen
 * renders that answer exactly as it renders the published-default one. There is still no
 * rate constant in this app and still no arithmetic over the roster here.
 *
 * They exist because the published row cannot answer MN's small-employer reduced rate
 * (0.66% combined: the employer pays 0.33%, not 0.44%), which is an employer-level
 * designation nothing in this repo can infer. When a figure differs from the published
 * one, the screen says so AND shows the published figure next to it — the server decides
 * which is which (`program.*Overridden`), so a typed rate can never be shown as a
 * sourced one.
 */

/** What the three boxes hold while they are being edited: strings, because that is what
 *  an <input> has. They become numbers only at the moment of submission. */
interface ProgramForm {
  rateEmployeePercent: string;
  rateEmployerPercent: string;
  wageBase: string;
}

/**
 * The inverse of the display-side percent scaling, applied on the way OUT to the engine:
 * the boxes collect a percentage because that is what a state calls its rate, and the
 * wire carries a fraction because that is what the engine has always taken. `toFixed(6)`
 * is not a rounding decision about the rate — it discards binary-float debris (0.33 / 100
 * is 0.0033000000000000004 in IEEE-754), which would otherwise hand the engine a
 * seventeen-digit rate that displays as a clean 0.33%. Six places holds any percentage
 * expressible to four decimals, far past any published figure.
 */
const fromPercent = (percent: number) => Number((percent / 100).toFixed(6));

/** A fraction as an editable percentage string — "0.0044" becomes "0.44". Trailing zeroes
 *  are trimmed so the box reads the way the state's own website writes the rate. */
function percentString(fraction: number): string {
  return String(Number((fraction * 100).toFixed(4)));
}

function formFromProgram(program: PaidLeaveProgramResponse): ProgramForm {
  return {
    rateEmployeePercent: percentString(program.rateEmployee),
    rateEmployerPercent: percentString(program.rateEmployer),
    wageBase: String(program.wageBase),
  };
}

/** Numbers, or null if any box does not hold one. Deliberately makes no judgment about
 *  whether a number is ACCEPTABLE — that is the engine endpoint's call. */
function parseForm(form: ProgramForm): PaidLeaveProgramOverrideRequest | null {
  const ee = Number(form.rateEmployeePercent.trim());
  const er = Number(form.rateEmployerPercent.trim());
  const base = Number(form.wageBase.trim());
  const blank =
    form.rateEmployeePercent.trim() === "" ||
    form.rateEmployerPercent.trim() === "" ||
    form.wageBase.trim() === "";

  if (blank || !Number.isFinite(ee) || !Number.isFinite(er) || !Number.isFinite(base)) return null;

  return { rateEmployee: fromPercent(ee), rateEmployer: fromPercent(er), wageBase: base };
}

export function PaidLeave() {
  /**
   * The parameters the engine is currently running with, or null for the published
   * defaults. Replaced only on an explicit Apply / Reset — a new object identity is what
   * makes the hook re-fire the whole run, so it must not be rebuilt on every render.
   */
  const [applied, setApplied] = useState<PaidLeaveProgramOverrideRequest | null>(null);
  const roster = usePayrollRoster();
  const { rows, status, data, error, rejected } = usePaidLeave(roster.employees, applied);
  const employeeCount = roster.employees?.length ?? 0;

  const wageDetail = data?.wageDetail ?? null;
  const failedRows = rows.filter((r) => r.status === "error");
  const notAssessed = rows.filter((r) => r.result?.success && r.result.premium?.applied === false);

  /** Display-only: the engine stores the rate as a fraction (0.0044). Scaling it for
   *  the label is formatting, the same class of transform as formatMoney — it is not
   *  this app deciding what the rate is. */
  const asPercent = (fraction: number) => `${(fraction * 100).toFixed(2)}%`;

  const [form, setForm] = useState<ProgramForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Seed the boxes from the FIRST answer the engine gives, and never again: after that
  // they hold what the employer is editing, and re-seeding on a later response would
  // yank the value out from under someone mid-type.
  useEffect(() => {
    if (form !== null || !data) return;
    setForm(formFromProgram(data.program));
  }, [data, form]);

  /**
   * The "Program in force" section's `Why?` disclosure (ADR 0009 §4) loads its prose
   * only once opened. `Why.tsx` (imported, not edited) keeps its children in the DOM
   * the whole time and hides them with CSS — correct for the common case, but this
   * section's longer explanation should not be sitting on the page — even invisibly —
   * before a reader asks for it. Gating the children on `whyOpen` makes "collapsed"
   * mean genuinely not-yet-rendered.
   *
   * Listens on the `<details>` element itself (`Why.tsx`'s own `.why` class) rather
   * than on a bubbled event, so it does not depend on whether this browser bubbles
   * the native `toggle` event — it is the event's target either way.
   */
  const whyRef = useRef<HTMLDivElement>(null);
  const [whyOpen, setWhyOpen] = useState(false);
  useEffect(() => {
    if (!data) return;
    const details = whyRef.current?.querySelector<HTMLDetailsElement>("details.why");
    if (!details) return;
    const onToggle = () => setWhyOpen(details.open);
    details.addEventListener("toggle", onToggle);
    return () => details.removeEventListener("toggle", onToggle);
  }, [data]);

  function applyForm(event: React.FormEvent): void {
    event.preventDefault();
    if (!form) return;

    // The ONLY check made here is "is this a number at all", because JSON cannot carry
    // an empty box or the word "abc" and there would be nothing to send. Every judgment
    // about whether a number is acceptable — sign, ceiling, a wage base of zero — belongs
    // to the engine's endpoint, which owns those rules and returns its own reason. A
    // second opinion in the browser would be a second, silently-drifting rulebook.
    const parsed = parseForm(form);
    if (!parsed) {
      setFormError(
        "Enter a number in each box. Leave a value as published rather than clearing it.",
      );
      return;
    }
    setFormError(null);
    setApplied(parsed);
  }

  function resetToPublished(): void {
    if (!data) return;
    setFormError(null);
    setForm({
      rateEmployeePercent: percentString(data.program.published.rateEmployee),
      rateEmployerPercent: percentString(data.program.published.rateEmployer),
      wageBase: String(data.program.published.wageBase),
    });
    setApplied(null);
  }

  function downloadWageDetail(): void {
    if (!wageDetail) return;
    // The bytes are the engine writer's output verbatim — this function adds a BOM-free
    // blob and a filename and nothing else. Never re-serialized from `records`, which
    // would make the download a second implementation of the file format.
    const blob = new Blob([wageDetail.delimitedFile], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = wageDetail.delimitedFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const quarterLabel = wageDetail ? `${wageDetail.year} Q${wageDetail.quarter}` : "";

  return (
    <main className="paid-leave">
      {/*
        THE BACK LINK IS GONE (ADR 0009 §2: "No back buttons — the tab strip says where
        you are"). The area tab strip below is now the way back to the returns row —
        select Returns, or any other payroll tab — and it renders on every payroll screen
        rather than needing its own bespoke "way back" per sub-screen.
      */}
      <PageHeader
        title="MN Paid Leave"
        subtitle={
          wageDetail
            ? `Sample pay period ${PAY_PERIOD_LABEL} · ${employeeCount} employees · reporting ${quarterLabel}`
            : roster.status === "ready"
              ? `Sample pay period ${PAY_PERIOD_LABEL} · ${employeeCount} employees`
              : `Sample pay period ${PAY_PERIOD_LABEL}`
        }
        action={
          <button
            type="button"
            className="btn btn--primary"
            disabled={!wageDetail}
            onClick={downloadWageDetail}
          >
            <Icon name="download" size={15} strokeWidth={2.2} />
            Download wage detail
          </button>
        }
      />

      <AreaTabs area="/payroll" tabs={PAYROLL_TABS} label="Payroll" />

      {roster.status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load the payroll roster."
          detail={`${roster.error ?? ""} Nothing below is computed — there is no honest paid-leave run without knowing who is on the payroll.`}
        />
      )}

      {roster.status === "ready" && employeeCount === 0 && (
        <StateMessage
          tone="empty"
          title="Nobody is on this entity's payroll yet."
          detail="An empty roster is a real answer, not a failure — there are no premiums to assess and no wage detail to file."
        />
      )}

      {status === "loading" && !data && roster.status !== "error" && (
        <StateMessage tone="loading" title="Calculating this quarter's paid-leave premiums…" />
      )}

      {error && rejected && (
        <StateMessage
          tone="error"
          title="The paid-leave engine rejected those program parameters."
          detail={
            data
              ? `${error} Nothing below has changed — the figures shown are still the ones the engine last accepted.`
              : error
          }
        />
      )}

      {error && !rejected && (
        <StateMessage
          tone="error"
          title="The paid-leave engine could not be reached."
          detail={error}
        />
      )}

      {data && (
        <>
          {/* ── what the engine is applying, editable in place ──────────────────────
              ADR 0009 §4 / ef-037c-4 — one number per field. This used to render
              the engine's own figure a SECOND time under every input, whether or
              not the employer had touched it — "0.44%" printed twice, once
              editable, once not. The box IS the one number for a field the engine
              is applying as published: `formFromProgram` seeds it from this same
              API response, so the input's own value already tells that story.
              The readout below an input now earns its keep only in the ONE state
              where a second number is genuinely useful — a figure the server
              says is OVERRIDDEN — where it shows what is currently APPLIED
              beside what Minnesota publishes, so an employer who typed 0.33%
              can still see the state's 0.44%. */}
          <section className="paid-leave__program" aria-labelledby="program-heading">
            <h2 id="program-heading" className="paid-leave__card-title">
              Program in force
            </h2>

            {form && (
              <form
                className="paid-leave__program-form"
                onSubmit={applyForm}
                noValidate
                aria-labelledby="program-heading"
              >
                <div className="paid-leave__program-grid">
                  <div className="paid-leave__field paid-leave__program-item">
                    <label htmlFor="rate-employee">Employee rate (%)</label>
                    <input
                      id="rate-employee"
                      name="rateEmployee"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={form.rateEmployeePercent}
                      onChange={(e) =>
                        setForm({ ...form, rateEmployeePercent: e.target.value })
                      }
                    />
                    {data.program.rateEmployeeOverridden && (
                      <p className="paid-leave__field-readout">
                        {asPercent(data.program.rateEmployee)}{" "}
                        <span className="paid-leave__badge paid-leave__badge--warn">
                          overridden
                        </span>{" "}
                        <span className="paid-leave__published">
                          published {asPercent(data.program.published.rateEmployee)}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="paid-leave__field paid-leave__program-item">
                    <label htmlFor="rate-employer">Employer rate (%)</label>
                    <input
                      id="rate-employer"
                      name="rateEmployer"
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={form.rateEmployerPercent}
                      onChange={(e) =>
                        setForm({ ...form, rateEmployerPercent: e.target.value })
                      }
                    />
                    {data.program.rateEmployerOverridden && (
                      <p className="paid-leave__field-readout">
                        {asPercent(data.program.rateEmployer)}{" "}
                        <span className="paid-leave__badge paid-leave__badge--warn">
                          overridden
                        </span>{" "}
                        <span className="paid-leave__published">
                          published {asPercent(data.program.published.rateEmployer)}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="paid-leave__field paid-leave__program-item">
                    <label htmlFor="wage-base">Annual wage base ($)</label>
                    <input
                      id="wage-base"
                      name="wageBase"
                      type="number"
                      step="1"
                      inputMode="decimal"
                      value={form.wageBase}
                      onChange={(e) => setForm({ ...form, wageBase: e.target.value })}
                    />
                    {/* "provisional" is a SEPARATE engine flag from "overridden" — the
                        cap can be an unreconciled estimate whether or not an employer
                        has typed their own figure, so it gets its own small flag next
                        to the box rather than living only inside the override readout. */}
                    {data.program.wageBaseIsProvisional && (
                      <p className="paid-leave__field-flag">
                        <span className="paid-leave__badge paid-leave__badge--warn">
                          provisional
                        </span>
                      </p>
                    )}
                    {data.program.wageBaseOverridden && (
                      <p className="paid-leave__field-readout">
                        {formatMoneyWhole(data.program.wageBase)}{" "}
                        <span className="paid-leave__badge paid-leave__badge--warn">
                          overridden
                        </span>{" "}
                        <span className="paid-leave__published">
                          published {formatMoneyWhole(data.program.published.wageBase)}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Not one of the three editable figures — the engine takes no
                      override for it, so it stays plain read-only text (a different
                      class from `.paid-leave__field-readout` on purpose: this value
                      never toggles, so it must never be counted among the readouts
                      the override state controls). */}
                  <div className="paid-leave__field paid-leave__program-item">
                    <span className="paid-leave__field-label">Effective</span>
                    <p className="paid-leave__field-value">
                      {data.program.effectiveOn.slice(0, 10)}
                    </p>
                  </div>
                </div>

                {formError && (
                  <p className="paid-leave__field-error" role="alert">
                    {formError}
                  </p>
                )}

                <div className="paid-leave__override-actions">
                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={status === "loading"}
                  >
                    Recalculate with these rates
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={resetToPublished}
                    disabled={status === "loading" || !data.program.isOverridden}
                  >
                    Reset to published
                  </button>
                </div>
              </form>
            )}

            {/* ADR 0009 §4 — "Prose is one line per section. Anything longer becomes
                a Why? disclosure the reader opens." Everything this paragraph used to
                say about editing, overrides and reconciliation moved into `Why`
                below; this line keeps only where the numbers above came from. */}
            <p className="paid-leave__note">
              {data.program.state} program parameters, read from the calculation engine — this
              screen keeps no copy of them.
            </p>
            <div ref={whyRef}>
              <Why>
                {whyOpen && (
                  <>
                    <p>
                      Change a figure above if it is not yours — a small employer pays a reduced
                      employer rate, and the state publishes new numbers each year. Whatever you
                      enter is what the engine calculates with, on this screen and in the file
                      you download.
                    </p>
                    {data.program.isOverridden && (
                      <p>
                        A <strong>badged</strong> figure above is this employer&apos;s own, not
                        Minnesota&apos;s; the state&apos;s published figure is shown beside it so
                        the two are never confused.
                        {/*
                          Deliberately avoids the word the BADGE itself renders. The badge is the
                          screen's one machine-readable claim that a figure is not the state's,
                          and a sentence repeating that word would make "how many figures are
                          badged?" unanswerable — to a test locator and to anyone scanning the
                          screen.
                        */}
                      </p>
                    )}
                    <p>
                      {data.program.wageBaseIsProvisional ? (
                        <>
                          The wage base is the engine&apos;s own stated approximation and is
                          <strong> not yet reconciled</strong> against Minnesota&apos;s published
                          2026 figure; it is flagged rather than presented as settled.
                        </>
                      ) : (
                        <>
                          They are <strong>reconciled</strong> against Minnesota Paid
                          Leave&apos;s published 2026 figures
                          {data.program.isOverridden
                            ? ", which is what the “published” figure beside each badged one is."
                            : ", so the cap above is the state's number and not an estimate."}
                          {/*
                            The sentence is qualified rather than dropped when a figure is
                            overridden. "Reconciled" is still TRUE of the published row — it is
                            just no longer true of the cap on screen, and a paragraph that kept
                            saying "the cap above is the state's number" over an employer's own
                            figure would be the one thing this screen must never do.
                          */}
                          {/*
                            Deliberately does NOT restate the rates or the cap. Every figure on
                            this screen comes from the API response, and a "helpful" paragraph
                            repeating them would put a second, silently-drifting copy in the
                            frontend — which is exactly what paid-leave.spec.ts's "not a copy in
                            the frontend" gate forbids. That gate caught this paragraph doing it.
                            The numbers live above; this sentence only says where they were
                            checked.
                          */}
                        </>
                      )}
                    </p>
                  </>
                )}
              </Why>
            </div>
          </section>

          {/* ── this pay period ─────────────────────────────────────────────────── */}
          <section className="paid-leave__totals-card" aria-labelledby="period-heading">
            <h2 id="period-heading" className="paid-leave__card-title">
              This pay period
            </h2>
            <dl className="paid-leave__totals">
              <div className="paid-leave__total-line">
                <dt>Covered wages</dt>
                <dd>{formatMoney(data.totals.coveredWages)}</dd>
              </div>
              <div className="paid-leave__total-line">
                <dt>Withheld from employees</dt>
                <dd className="paid-leave__deduct">−{formatMoney(data.totals.employeePremium)}</dd>
              </div>
              <div className="paid-leave__total-line">
                <dt>Employer share</dt>
                <dd>{formatMoney(data.totals.employerPremium)}</dd>
              </div>
              <div className="paid-leave__total-line paid-leave__total-line--big">
                <dt>Owed to the state</dt>
                <dd>{formatMoney(data.totals.totalRemittance)}</dd>
              </div>
            </dl>
            <p className="paid-leave__note">
              {data.totals.appliedCount} of {employeeCount} employees assessed
              {data.totals.notAppliedCount > 0 &&
                `, ${data.totals.notAppliedCount} not assessed`}
              {data.totals.failedCount > 0 && `, ${data.totals.failedCount} could not be computed`}.
            </p>
          </section>

          {/* ── the file the state actually wants ───────────────────────────────── */}
          {wageDetail && (
            <section className="paid-leave__detail" aria-labelledby="detail-heading">
              <div className="paid-leave__detail-head">
                <h2 id="detail-heading" className="paid-leave__card-title">
                  Quarterly wage detail · {quarterLabel}
                </h2>
                <p className="paid-leave__note">
                  What Minnesota asks for every quarter, rolled up per employee by the same
                  engine — built from {wageDetail.lineCount}{" "}
                  {wageDetail.lineCount === 1 ? "paycheck line" : "paycheck lines"}
                  {wageDetail.linesOutsideQuarter > 0 &&
                    `, with ${wageDetail.linesOutsideQuarter} outside the quarter excluded`}
                  .
                </p>
                {/*
                  WHAT THE FILE IS, SAID ON THE SCREEN THAT PRODUCES IT.

                  MEASURED 2026-09-08 by downloading it from the Live Wall. The premium
                  arithmetic is right and the column headers are Minnesota's own, which is
                  exactly what makes it dangerous: it looks fileable. Two columns are not.

                      SSN,LastName,FirstName,TotalWages,...
                      "190b6a66-a943-3992-137f-d554fa335b9e","Rosa M.","",1088.00,...

                  The SSN column carries the employee's internal id — a deliberate choice,
                  documented in PaidLeaveEndpoints.cs, so the substitution is visible rather
                  than disguised as a nine-digit number. And this roster has display names
                  only ("Rosa M." is a first name and an initial), so the whole name lands in
                  LastName and FirstName is empty.

                  The SCREEN was already honest about the first — its SSN column reads "not
                  on file" for every row. The FILE said something different, and the file is
                  the half that leaves the building. The Reports screen next door is
                  scrupulous about exactly this for its own export ("FINPACK export ·
                  preview", "no certified FINPACK integration behind it yet"); this screen,
                  whose output looks far more like a real submission, said nothing at all.

                  Stated here rather than after the download, because a person needs it
                  BEFORE they hand the file to the state, not after it is on their disk.
                */}
                <p className="paid-leave__note" data-state="preview-caveat">
                  <strong>The download is a preview.</strong> A real submission needs each
                  employee&apos;s Social Security number, and their name split into first and
                  last. This demo roster has neither — so in the file the SSN column carries
                  an internal id, and the whole name sits in the last-name field.
                </p>
              </div>

              {/* tabIndex + role/name: a horizontally scrollable region must be
                  reachable and scrollable by keyboard, or a keyboard-only user simply
                  cannot see the right-hand columns. Caught by the axe gate as a SERIOUS
                  violation on the phone viewport, where the table actually overflows. */}
              <div
                className="paid-leave__table-scroll"
                tabIndex={0}
                role="region"
                aria-labelledby="detail-heading"
              >
                <table className="paid-leave__table ruled-rows">
                  <caption className="visually-hidden">
                    {quarterLabel} Minnesota Paid Leave wage detail, one row per employee
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Employee</th>
                      <th scope="col">SSN</th>
                      <th scope="col" className="paid-leave__num">Total wages</th>
                      <th scope="col" className="paid-leave__num">Covered wages</th>
                      {/*
                        "Employee share" / "Employer share", not "Employee" / "Employer".
                        Column 1 is already <th>Employee</th> — the PERSON — so a bare
                        "Employee" here gave two column headers the same accessible name
                        meaning different things: one names who the row is about, the other
                        is that person's premium. A screen reader announces both cells as
                        "Employee", and getByRole("columnheader", { name: "Employee" })
                        matches two elements.

                        On a phone the ambiguity is worse than an inconvenience: the table
                        is 640px in a 343px scroller, so these columns are read AFTER
                        scrolling right, with column 1 no longer on screen to disambiguate
                        by position.

                        "share" is the page's own existing vocabulary — the summary block
                        above already says "Withheld from employees" and "Employer share" —
                        so this aligns the table with the words beside it rather than
                        inventing new ones. The export is unaffected: it does not use these
                        strings.
                      */}
                      <th scope="col" className="paid-leave__num">Employee share</th>
                      <th scope="col" className="paid-leave__num">Employer share</th>
                      <th scope="col" className="paid-leave__num">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wageDetail.records.map((record) => (
                      <tr key={record.ssn}>
                        <th scope="row">{record.lastName ?? record.ssn}</th>
                        <td>
                          {record.ssnSupplied ? (
                            record.ssn
                          ) : (
                            <span className="paid-leave__missing">not on file</span>
                          )}
                        </td>
                        <td className="paid-leave__num paid-leave__amount">{formatMoney(record.totalWages)}</td>
                        <td className="paid-leave__num paid-leave__amount">{formatMoney(record.pflCoveredWages)}</td>
                        <td className="paid-leave__num paid-leave__amount">{formatMoney(record.pflEmployeePremium)}</td>
                        <td className="paid-leave__num paid-leave__amount">{formatMoney(record.pflEmployerPremium)}</td>
                        <td className="paid-leave__num">
                          {/* `!= null`, not `> 0`: a person who genuinely worked zero
                              hours is a known zero, and reading it as "unknown" is the same
                              conflation this column exists to avoid. */}
                          {record.hours != null ? (
                            record.hours
                          ) : (
                            <span className="paid-leave__missing">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row" colSpan={3}>
                        Quarter total
                      </th>
                      <td className="paid-leave__num paid-leave__amount">{formatMoney(wageDetail.totalCoveredWages)}</td>
                      <td className="paid-leave__num paid-leave__amount" colSpan={2}>
                        {formatMoney(wageDetail.totalRemittance)} remittance
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          )}

          {/* ── lines the engine could not assess ───────────────────────────────── */}
          {failedRows.length > 0 && (
            <StateMessage
              tone="error"
              title={`${failedRows.length} of ${rows.length} paycheck lines could not be assessed.`}
              detail={`They are excluded from the totals and from the wage-detail file above — those figures are a real subtotal, not the whole roster. ${failedRows
                .map((r) => `${r.employee.name}: ${r.error}`)
                .join(" ")}`}
            />
          )}

          {notAssessed.length > 0 && (
            <StateMessage
              tone="empty"
              title={`${notAssessed.length} employee(s) computed to no premium.`}
              detail="The engine assessed nothing for them — exempt, paid before the program's effective date, or already over the annual wage base. They still appear on the wage detail with their total wages, which is what the state's report asks for."
            />
          )}

          {/* ── what this screen does not know ──────────────────────────────────── */}
          <section className="paid-leave__disclosure" aria-labelledby="gaps-heading">
            <h2 id="gaps-heading" className="paid-leave__disclosure-title">
              What this file still needs before it could be filed
            </h2>
            <ul className="paid-leave__gaps">
              {wageDetail && wageDetail.ssnPopulatedCount < wageDetail.records.length && (
                <li>
                  <strong>
                    Social Security numbers ({wageDetail.ssnPopulatedCount} of{" "}
                    {wageDetail.records.length} on file).
                  </strong>{" "}
                  Minnesota keys the wage detail on SSN and the demo roster carries none, so the
                  rows above are grouped by employee id instead. That is deliberately visible in
                  the generated file rather than filled with something SSN-shaped.
                </li>
              )}
              {!wageDetail?.employerAccountId && (
                <li>
                  <strong>The employer's joint UI–Paid Leave account number.</strong> Not
                  configured in this demo; the file carries no account id.
                </li>
              )}
              {wageDetail && wageDetail.hoursPopulatedCount < wageDetail.lineCount && (
                <li>
                  <strong>
                    Hours for {wageDetail.lineCount - wageDetail.hoursPopulatedCount} of{" "}
                    {wageDetail.lineCount} lines.
                  </strong>{" "}
                  The roster states hours for its hourly employees and not for salaried, H-2A or
                  seasonal ones. Those are shown as "—" rather than zero: the Hours column above
                  is understated, not a claim that nobody worked.
                </li>
              )}
              {wageDetail?.columnOrderIsProvisional && (
                <li>
                  <strong>Column order confirmation.</strong> The data is firm; the delimited
                  file's field ORDER is marked provisional in the engine and must be aligned to
                  Minnesota's own template before a real submission.
                </li>
              )}
              <li>
                <strong>Whether H-2A workers are exempt from this program.</strong> The engine
                takes a per-employee exemption flag and the roster knows who is H-2A — but FICA
                exemption and paid-leave exemption are separate determinations under separate
                law, and this demo has not made the second one. Every line above is therefore
                assessed as non-exempt. This is the same honesty the Payroll screen applies to
                the H-2A FICA gap: a stated open question, not a quiet assumption in either
                direction.
              </li>
              <li>
                <strong>A full quarter, and year-to-date wages.</strong> The demo roster is a
                single pay period with no prior history, so the quarter above holds one period
                rather than a quarter's worth, and the annual wage-base cap is never reached. The
                engine applies that cap — it simply has nothing here to apply it to.
              </li>
            </ul>
          </section>
        </>
      )}
    </main>
  );
}
